import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import Delete from '@mui/icons-material/Delete';
import Science from '@mui/icons-material/Science';
import { apiClient, endpoints } from '../api/client';
import { useExtensionWorkers } from '../hooks/useExtensionWorkers';
import { useMarketplaceDefinitions } from '../hooks/useMarketplaceDefinitions';
import {
  createDefinitionTestSuite,
  DefinitionTestSuiteSummary,
  deleteDefinitionTestSuite,
  useDefinitionTestSuiteList,
} from '../hooks/useDefinitionTestSuites';
import { SuggestedKeyword } from '../hooks/useTaskGroups';
import { getCurrentProviderId } from '../utils/currentUser';
import { loadStoreBrandNames, loadStoreProductsForBrand } from '../utils/storeCatalog';

function errorText(err: any, fallback: string): string {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return String(detail[0].msg);
  return err?.message || fallback;
}

function formatWhen(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function statusColor(status: string): 'default' | 'info' | 'success' | 'warning' | 'error' {
  if (status === 'completed') return 'success';
  if (status === 'failed') return 'error';
  if (status === 'preparing' || status === 'running') return 'info';
  return 'default';
}

const DefinitionTestSuites: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data, isLoading, error, refetch } = useDefinitionTestSuiteList();
  const { data: definitions = [] } = useMarketplaceDefinitions();
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DefinitionTestSuiteSummary | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    const state = location.state as { create?: boolean } | null;
    if (state?.create) {
      setCreateOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  const rows = data?.items || [];
  const columns: GridColDef<DefinitionTestSuiteSummary>[] = [
    {
      field: 'brand_name',
      headerName: 'Brand',
      flex: 0.8,
      minWidth: 120,
    },
    {
      field: 'keyword',
      headerName: 'Keyword',
      flex: 1,
      minWidth: 160,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: params => (
        <Chip size="small" label={params.value} color={statusColor(String(params.value))} />
      ),
    },
    {
      field: 'progress',
      headerName: 'Progress',
      width: 120,
      valueGetter: params => `${params.row.finished_count}/${params.row.item_count || '…'}`,
    },
    {
      field: 'created_at',
      headerName: 'Started',
      flex: 0.8,
      minWidth: 170,
      valueGetter: params => formatWhen(params.row.created_at),
    },
    {
      field: 'actions',
      headerName: '',
      width: 140,
      sortable: false,
      renderCell: params => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button size="small" onClick={() => navigate(`/test-suites/${params.row.id}`)}>
            Report
          </Button>
          <IconButton
            size="small"
            aria-label={`Delete ${params.row.brand_name} test suite`}
            onClick={event => {
              event.stopPropagation();
              setDeleteError(null);
              setDeleteTarget(params.row);
            }}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Science color="primary" />
        <Typography variant="h5" sx={{ flex: 1 }}>
          Marketplace test suites
        </Typography>
        <Button variant="contained" onClick={() => setCreateOpen(true)}>
          New test suite
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Run one keyword on every marketplace definition with the Chrome extension, then use the
        report to see which definitions extract a product page and which fields are missing.
      </Typography>
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load test suites.
        </Alert>
      ) : null}
      <Box sx={{ height: 560, width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={isLoading}
          disableRowSelectionOnClick
          onRowClick={(params, event) => {
            const target = event.target as HTMLElement | null;
            if (target?.closest('button')) return;
            navigate(`/test-suites/${params.row.id}`);
          }}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
          sx={{ cursor: 'pointer' }}
        />
      </Box>
      <Dialog open={Boolean(deleteTarget)} onClose={() => !deleting && setDeleteTarget(null)}>
        <DialogTitle>Delete test suite?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This removes the {deleteTarget?.brand_name} run for “{deleteTarget?.keyword}” and its
            scraping tasks. Marketplace definitions are kept.
          </Typography>
          {deleteError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleting || !deleteTarget}
            onClick={async () => {
              if (!deleteTarget) return;
              setDeleting(true);
              setDeleteError(null);
              try {
                await deleteDefinitionTestSuite(deleteTarget.id);
                setDeleteTarget(null);
                await refetch();
              } catch (err: any) {
                setDeleteError(errorText(err, 'Delete failed'));
              } finally {
                setDeleting(false);
              }
            }}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      <CreateSuiteDialog
        open={createOpen}
        definitionCount={definitions.filter(def => !def.is_template).length}
        definitionsCapped={definitions.length >= 500}
        onClose={() => setCreateOpen(false)}
        onCreated={id => {
          setCreateOpen(false);
          refetch();
          navigate(`/test-suites/${id}`);
        }}
      />
    </Box>
  );
};

interface DialogProps {
  open: boolean;
  definitionCount: number;
  definitionsCapped: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}

const CreateSuiteDialog: React.FC<DialogProps> = ({
  open,
  definitionCount,
  definitionsCapped,
  onClose,
  onCreated,
}) => {
  const { data: workers } = useExtensionWorkers(open);
  const [brands, setBrands] = useState<string[]>([]);
  const [brand, setBrand] = useState('');
  const [keyword, setKeyword] = useState('');
  const [keywordTouched, setKeywordTouched] = useState(false);
  const [suggestions, setSuggestions] = useState<SuggestedKeyword[]>([]);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestError, setSuggestError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setBrand('');
    setKeyword('');
    setKeywordTouched(false);
    setSuggestions([]);
    setSuggestError(null);
    setSubmitError(null);
    loadStoreBrandNames()
      .then(setBrands)
      .catch(() => setBrands([]));
  }, [open]);

  useEffect(() => {
    if (!keywordTouched) setKeyword(brand);
  }, [brand, keywordTouched]);

  const onlineWorkers = workers?.summary?.online ?? 0;
  const targetLabel = definitionsCapped
    ? 'every non-template marketplace definition'
    : `${definitionCount} marketplace definition${definitionCount === 1 ? '' : 's'}`;

  const canStart = Boolean(brand.trim()) && Boolean(keyword.trim()) && !submitting;

  const productLineSuggestions = useMemo(
    () => suggestions.filter(item => item.kind !== 'bait'),
    [suggestions]
  );

  const handleSuggest = async () => {
    if (!brand) return;
    setSuggesting(true);
    setSuggestError(null);
    try {
      const products = await loadStoreProductsForBrand(brand);
      const response = await apiClient.post(endpoints.taskGroupSuggest, {
        brand_name: brand,
        products,
      });
      const keywords = (response.data?.keywords || []) as SuggestedKeyword[];
      setSuggestions(keywords.filter(item => item.kind !== 'bait'));
      const first = keywords.find(item => item.kind !== 'bait');
      if (first && !keywordTouched) {
        setKeyword(first.text);
        setKeywordTouched(true);
      }
      if (!keywords.length) setSuggestError('No product-line keywords were suggested.');
    } catch (err: any) {
      setSuggestError(errorText(err, 'Could not suggest keywords'));
    } finally {
      setSuggesting(false);
    }
  };

  const handleStart = async () => {
    if (!canStart) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const createdBy = await getCurrentProviderId();
      const created = await createDefinitionTestSuite({
        brand_name: brand.trim(),
        keyword: keyword.trim(),
        ...(createdBy ? { created_by: createdBy } : {}),
      });
      onCreated(created.id);
    } catch (err: any) {
      setSubmitError(errorText(err, 'Could not start the test suite'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Test every marketplace</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
          Queues one Chrome extension task per marketplace: a single search for this keyword, one
          results page, and at most one stage 2 product. The report shows which definitions worked,
          which fields came back, and why the others failed.
        </Typography>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Brand</InputLabel>
          <Select
            value={brand}
            label="Brand"
            onChange={event => setBrand(String(event.target.value))}
          >
            <MenuItem value="">Select brand</MenuItem>
            {brands.map(name => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <TextField
            fullWidth
            label="Keyword"
            value={keyword}
            onChange={event => {
              setKeywordTouched(true);
              setKeyword(event.target.value);
            }}
            helperText="One search query, sent to every marketplace"
          />
          <Button
            variant="outlined"
            startIcon={suggesting ? <CircularProgress size={16} /> : <AutoAwesome />}
            onClick={handleSuggest}
            disabled={!brand || suggesting}
            sx={{ whiteSpace: 'nowrap', height: 56 }}
          >
            Suggest
          </Button>
        </Box>
        {suggestError && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {suggestError}
          </Alert>
        )}
        {productLineSuggestions.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {productLineSuggestions.map(item => (
              <Chip
                key={item.text}
                label={item.text}
                color={
                  keyword.trim().toLowerCase() === item.text.toLowerCase() ? 'primary' : 'default'
                }
                variant={
                  keyword.trim().toLowerCase() === item.text.toLowerCase() ? 'filled' : 'outlined'
                }
                onClick={() => {
                  setKeywordTouched(true);
                  setKeyword(item.text);
                }}
              />
            ))}
          </Box>
        )}
        <Alert severity="info" sx={{ mb: 1 }}>
          This will queue extension tasks for {targetLabel}. Tasks stay off the main task list and
          are tracked on the suite report.
        </Alert>
        {onlineWorkers === 0 && (
          <Alert severity="warning">
            No Chrome extension workers are online. Tasks will wait until a signed-in Collector
            extension is listening.
          </Alert>
        )}
        {submitError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {submitError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleStart} disabled={!canStart}>
          {submitting ? 'Starting…' : 'Start test suite'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DefinitionTestSuites;
